"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NumberIncrementField = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const material_1 = require("@mui/material");
const ArrowDropUp_1 = __importDefault(require("@mui/icons-material/ArrowDropUp"));
const ArrowDropDown_1 = __importDefault(require("@mui/icons-material/ArrowDropDown"));
/**
 *
 * @component
 * @prop {(value: string, number: BigNumber) => void} onChange
 * - is a function that runs when the value changes
 * - the value is passed to the function as a string and BigNumber so it can be used outside of the component
 * @prop {boolean} disallowNegative
 * @prop {boolean} noDecimals
 * @prop {"left-input" | "middle-input"} [variant="left-input"] decides the layout of the input
 * @prop {string | number } [width="200px"]
 * @prop {string} [color=secondary.main]
 * @prop {number} [value=0] sets the value inside the component
 */
const NumberIncrementField = ({ onChange, disallowNegative = false, noDecimals, disabled = false, variant = "left-input", width = "200px", color = "#30b0b0", value = 0, id = "", }) => {
    const [stringValue, setStringValue] = (0, react_1.useState)(value ? `${value}` : "");
    const [bigNumber, setBigNumber] = (0, react_1.useState)(new bignumber_js_1.default(value));
    const [_color, setColor] = (0, react_1.useState)(color);
    (0, react_1.useEffect)(() => {
        if (!color)
            return;
        setColor(color);
    }, [color]);
    const regexExp = (0, react_1.useMemo)(() => {
        const negative = disallowNegative ? "" : "[-]?";
        const number = noDecimals ? "\\d+" : "(\\d+(\\.\\d*)?|\\.\\d+)";
        return new RegExp(`^${negative}${number}$`);
    }, [noDecimals, disallowNegative]);
    const getValue = () => {
        if (new bignumber_js_1.default(stringValue).isNaN()) {
            return stringValue;
        }
        return bigNumber.toFormat();
    };
    const increment = () => {
        const newVal = bigNumber.plus(1);
        setStringValue(newVal.toString());
        setBigNumber(newVal);
        if (onChange) {
            onChange(newVal.toString(), newVal);
        }
    };
    const decrement = () => {
        if (disallowNegative && bigNumber.eq(0))
            return;
        const newVal = bigNumber.minus(1);
        setStringValue(newVal.toString());
        setBigNumber(newVal);
        if (onChange) {
            onChange(newVal.toString(), newVal);
        }
    };
    const onInputChange = (e) => {
        const v = e.target.value.replace(",", "");
        if (v === "") {
            setStringValue("");
            setBigNumber(new bignumber_js_1.default(0));
        }
        else if (!disallowNegative && v === "-") {
            setStringValue(v);
            setBigNumber(new bignumber_js_1.default(0));
        }
        else if (regexExp.test(v)) {
            setStringValue(v);
            setBigNumber(new bignumber_js_1.default(v));
        }
        if (onChange) {
            onChange(v, new bignumber_js_1.default(v));
        }
    };
    switch (variant) {
        case "left-input":
            return ((0, jsx_runtime_1.jsxs)(material_1.FormControl, { disabled: disabled, sx: (theme) => ({
                    width: width,
                    height: "40px",
                    minWidth: width,
                    display: "grid",
                    gridTemplateColumns: "auto 34px",
                    border: `solid 1px ${_color}`,
                    borderRadius: "4px",
                    transition: "background-color 200ms ease-in-out",
                    "&:disabled": {
                        border: `solid 1px ${theme.palette.text.disabled}`,
                    },
                    "&:focus-within": {
                        backgroundColor: (0, material_1.alpha)(_color, 0.1),
                    },
                }), onKeyDown: (e) => {
                    if (e.key === "ArrowUp") {
                        increment();
                    }
                    else if (e.key === "ArrowDown") {
                        decrement();
                    }
                }, children: [(0, jsx_runtime_1.jsx)(material_1.InputBase, { id: id, inputProps: {
                            pattern: "\\d*",
                            style: { textAlign: "center" },
                        }, placeholder: "0", value: getValue(), className: "input", margin: "none", onChange: onInputChange }), (0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                            display: "grid",
                            gridTemplateRows: "auto auto",
                        }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { component: material_1.ButtonBase, disabled: disabled, sx: (theme) => ({
                                    backgroundColor: (0, material_1.alpha)(_color, 0.3),
                                    borderLeft: `solid 0.5px ${_color}`,
                                    "&:disabled": {
                                        borderLeft: `solid 0.5px ${theme.palette.text.disabled}`,
                                        backgroundColor: (0, material_1.alpha)("#FFFFFF", 0.1),
                                    },
                                    "&:hover": {
                                        backgroundColor: (0, material_1.alpha)(_color, 0.4),
                                    },
                                }), onClick: increment, children: (0, jsx_runtime_1.jsx)(ArrowDropUp_1.default, { sx: { color: _color, height: "18px" } }) }), (0, jsx_runtime_1.jsx)(material_1.Box, { component: material_1.ButtonBase, disabled: disabled, sx: (theme) => ({
                                    backgroundColor: (0, material_1.alpha)(_color, 0.3),
                                    borderLeft: `solid 0.5px ${_color}`,
                                    borderTop: `solid 0.5px ${_color}`,
                                    "&:disabled": {
                                        borderLeft: `solid 0.5px ${theme.palette.text.disabled}`,
                                        borderTop: `solid 0.5px ${theme.palette.text.disabled}`,
                                        backgroundColor: (0, material_1.alpha)("#FFFFFF", 0.1),
                                    },
                                    "&:hover": {
                                        backgroundColor: (0, material_1.alpha)(_color, 0.4),
                                    },
                                }), onClick: decrement, children: (0, jsx_runtime_1.jsx)(ArrowDropDown_1.default, { sx: { color: _color, height: "18px" } }) })] })] }));
        case "middle-input":
            return ((0, jsx_runtime_1.jsxs)(material_1.FormControl, { disabled: disabled, sx: (theme) => ({
                    width: width,
                    display: "grid",
                    height: "40px",
                    minWidth: width,
                    gridTemplateColumns: "34px auto 34px",
                    border: `solid 1px ${_color}`,
                    borderRadius: "4px",
                    transition: "background-color 200ms ease-in-out",
                    "&:disabled": {
                        border: `solid 1px ${theme.palette.text.disabled}`,
                    },
                    "&:focus-within": {
                        backgroundColor: (0, material_1.alpha)(_color, 0.1),
                    },
                }), children: [(0, jsx_runtime_1.jsx)(material_1.Box, { component: material_1.ButtonBase, sx: {
                            backgroundColor: (0, material_1.alpha)(_color, 0.3),
                            borderRight: `solid 0.5px ${_color}`,
                            "&:hover": {
                                backgroundColor: (0, material_1.alpha)(_color, 0.4),
                            },
                        }, onClick: () => {
                            if (disallowNegative && bigNumber.eq(0))
                                return;
                            const newVal = bigNumber.minus(1);
                            setStringValue(newVal.toString());
                            setBigNumber(newVal);
                            if (onChange) {
                                onChange(newVal.toString(), newVal);
                            }
                        }, children: (0, jsx_runtime_1.jsx)(ArrowDropDown_1.default, { sx: { color: _color } }) }), (0, jsx_runtime_1.jsx)(material_1.InputBase, { inputProps: {
                            pattern: "\\d*",
                            style: { textAlign: "center" },
                        }, placeholder: "0", value: getValue(), className: "input", margin: "none", onChange: onInputChange }), (0, jsx_runtime_1.jsx)(material_1.Box, { component: material_1.ButtonBase, sx: {
                            backgroundColor: (0, material_1.alpha)(_color, 0.3),
                            borderLeft: `solid 0.5px ${_color}`,
                            "&:hover": {
                                backgroundColor: (0, material_1.alpha)(_color, 0.4),
                            },
                        }, onClick: () => {
                            const newVal = bigNumber.plus(1);
                            setStringValue(newVal.toString());
                            setBigNumber(newVal);
                            if (onChange) {
                                onChange(newVal.toString(), newVal);
                            }
                        }, children: (0, jsx_runtime_1.jsx)(ArrowDropUp_1.default, { sx: { color: _color } }) })] }));
    }
};
exports.NumberIncrementField = NumberIncrementField;
